/*
 * SundayBuilder · Start-Over Confirmation
 * ---------------------------------------------------------------------
 * Guards against accidentally discarding an in-progress draft.
 * Default focus is "Keep draft" — the destructive path is deliberate.
 * =================================================================== */
import { useEffect, useRef } from "react";

function _prettyTimestamp(iso) {
  if (!iso) return "recently";
  try {
    const d = new Date(iso);
    const opts = { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" };
    return d.toLocaleString(undefined, opts);
  } catch {
    return "recently";
  }
}

export default function StartOverConfirm({ savedAt, occasion, onKeep, onDiscard }) {
  const keepRef = useRef(null);

  useEffect(() => {
    keepRef.current?.focus();
    const esc = (e) => { if (e.key === "Escape") onKeep(); };
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [onKeep]);

  return (
    <div
      className="sb-modal-backdrop"
      onClick={onKeep}
      role="dialog"
      aria-modal="true"
      aria-labelledby="sb-start-over-modal-title"
      data-testid="sb-start-over-confirm-modal"
    >
      <div className="sb-modal" onClick={(e) => e.stopPropagation()}>
        <h2 id="sb-start-over-modal-title" className="sb-modal__title">
          Discard the current draft?
        </h2>
        <p className="sb-modal__body">
          Your {occasion || "Sunday"} draft was last saved{" "}
          {savedAt ? `at ${_prettyTimestamp(savedAt)}` : "recently"}.
          This action cannot be undone.
        </p>
        <div className="sb-modal__actions">
          <button
            ref={keepRef}
            className="sb-btn ghost"
            onClick={onKeep}
            data-testid="sb-start-over-keep"
          >
            Keep draft
          </button>
          <button
            className="sb-btn destructive"
            onClick={onDiscard}
            data-testid="sb-start-over-discard"
          >
            Discard and start over
          </button>
        </div>
      </div>
    </div>
  );
}
